import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './Section.scss';

function CustomSection({
  title = "",
  required = false,
  containerStyles = "", 
  type = "",
  checkInvalid = false,
  setInvalidConditions = (() => { return }),
  setFormData,
  formData,
  conditionProperties = {},
  customContent = null,
  triggerInitialiseForm,
  stateId = null,
  valueType = 'string',
}) {

  const randomId = uuidv4();

  const initialValue = valueType === 'array' ? [] :
    valueType === 'object' ? {} :
    valueType === 'file' ? null :
    valueType === 'bool' ? false :
    '';
  
  const [value, setValue] = useState(initialValue);

  const handleSetValue = ({ argValue = initialValue, initialise = false } = {}) => {
    // value type is Array
    if (valueType === 'array') {
      setValue(prev => initialise ? [] : [...prev, argValue])
      return;
    }
    // value type is Object
    // requires stateId prop value as key for new object entries
    if (valueType === 'object') {
      if (!stateId) { return };
      setValue(prev => initialise ? {} : { ...prev, [stateId]: argValue ?? ''})
      return;
    }
    // value type is String
    if (valueType === 'string') {
      setValue(() => initialise ? '' : argValue)
      return;
    }
    
    // value type is File
    if (valueType === 'file') {
      setValue(() => initialise ? null : argValue)
      return;
    }
    
    // value type is Bool
    if (valueType === 'bool') {
      setValue(() => initialise ? false : argValue)
      return;
    }
  }

  const [photoFile, setPhotoFile] = useState(null);
  const [photoFileInvalid, setPhotoFileInvalid] = useState(false);

  useEffect(() => {
    if (triggerInitialiseForm) {
      handleSetValue({ argValue: initialValue, initialise: true });
      setPhotoFile(null);
      setPhotoFileInvalid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerInitialiseForm, initialValue])

  // sets invalid condition for required value if null
  const isEmpty = (v) => {
    if (v === '' || v === null || v === undefined || v === false) return true;
    if (Array.isArray(v)) return v.length === 0;
    if (v instanceof File || v instanceof Blob) return false;
    if (typeof v === 'object') return Object.keys(v).length === 0;
    return false;
  };

  const evaluateCondition = (data, { key, type, condition }) => {
    if (!data || !key || !type || !condition) return null;
    const target = data[key];
    if (target === undefined || target === null) return null;
    const operand = type === 'length' ? target.length : target?.[type];
    const match = condition.trim().match(/^(>=|<=|===|!==|>|<)\s*(.+)$/);
    if (!match) return null;
    const [, operator, rhsRaw] = match;
    const rhs = isNaN(Number(rhsRaw)) ? rhsRaw : Number(rhsRaw);
    switch (operator) {
      case '>':  return operand > rhs;
      case '<':  return operand < rhs;
      case '>=': return operand >= rhs;
      case '<=': return operand <= rhs;
      case '===': return operand === rhs;
      case '!==': return operand !== rhs;
      default:   return null;
    }
  };

  const { 
    conditionKey, 
    conditionType, 
    conditionRequirement 
  } = conditionProperties ?? {};

  useEffect(() => {
    if (!checkInvalid) return;
    
    const isFileInvalid = photoFileInvalid !== false;
    const conditionResult = evaluateCondition(formData, {
      key: conditionKey,
      type: conditionType,
      condition: conditionRequirement,
    });

    const hasConditionProps = !!(conditionKey && conditionType && conditionRequirement);

    setInvalidConditions((prev) => ({
      ...prev,
      [title || conditionKey]: {
        condition:
          isFileInvalid ||
          (required && !hasConditionProps && isEmpty(value)) ||
          (required && hasConditionProps && conditionResult !== true),
        message: {
          title,
          message: isFileInvalid
            ? typeof photoFileInvalid !== 'string'
              ? 'format is invalid'
              : photoFileInvalid
            : 'is required',
          id: randomId,
        },
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value, 
    photoFileInvalid, 
    photoFile, 
    checkInvalid, 
    required, 
    formData, 
    conditionKey, 
    conditionType, 
    conditionRequirement,
  ]);

    useEffect(() => {
    setFormData(prev => {  
      if (!title) return prev;
      
      const modifiedTitle = title?.toLowerCase()?.replaceAll(' ', '_');
      return {
        ...prev,
        [modifiedTitle]: value, 
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, photoFileInvalid, photoFile, checkInvalid, required])

  return (
    <div
      key={`${title}_${type}_element`}
      className={
        `
          custom-form-section custom-section
          ${!type ? 'standard' : ''}
          ${(containerStyles) ?? ""}
          w-100
        `
      }
    >
      <>
        {customContent}
      </>
    </div>
  )
}

export default CustomSection;
